import Image, { StaticImageData } from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { ChangeEvent, Dispatch, ReactNode, SetStateAction, useRef } from 'react';
import CameraIcon from '@/components/icons/CameraIcon';
import RightIcon from '@/components/icons/RightIcon';
import WrongIcon from '@/components/icons/WrongIcon';
import Paragraph, { VARIANT } from '@/components/Paragraph';
import SubHeading from '@/components/SubHeading';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import DiseaseBlur from '@/images/ask-question/disease_blur.webp';
import DiseaseClear from '@/images/ask-question/disease_clear.webp';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
interface SampleImageProps {
  bottomBorderColour: string;
  icon: ReactNode;
  image: StaticImageData;
  text: string;
}
const SampleImage = ({
  bottomBorderColour,
  icon,
  image,
  text
}: SampleImageProps) => {
  return <div className={`basis-1 border border-b-4 border-neutral-40 grow-0 h-fit p-4 rounded-t-lg ${bottomBorderColour}`} data-sentry-component="SampleImage" data-sentry-source-file="DiseaseUpload.tsx">
      {icon}
      <Paragraph className="min-h-[2.5rem] mt-4 text-center text-neutral-80" isHighlighted variant={VARIANT.MEDIUM} data-sentry-element="Paragraph" data-sentry-source-file="DiseaseUpload.tsx">
        {text}
      </Paragraph>
      <div className="relative w-32 h-32 m-auto mt-4 overflow-hidden rounded-md">
        <Image alt={text} src={image} fill data-sentry-element="Image" data-sentry-source-file="DiseaseUpload.tsx" />
      </div>
    </div>;
};
interface Props {
  setCurrentCrumbIndex: Dispatch<SetStateAction<number>>;
  setDiseaseImages: Dispatch<SetStateAction<Blob[]>>;
}
const DiseaseUpload = ({
  setCurrentCrumbIndex,
  setDiseaseImages
}: Props) => {
  const diseaseInputRef = useRef<HTMLInputElement>(null);
  const {
    t
  } = useTranslation('askQuestion');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length === 1) {
      const file = e.target.files[0];
      setDiseaseImages([file]);
      setCurrentCrumbIndex(1);
      trackCustomEvent(CustomEventTypes.ASK_HELP_IMAGE_UPLOADED);
    }
  };
  return <>
      <SubHeading className="px-4 py-6 text-center bg-white" data-sentry-element="SubHeading" data-sentry-source-file="DiseaseUpload.tsx">
        {t('take_close_up')}
      </SubHeading>
      <div className="flex justify-center gap-4 bg-white grow">
        <SampleImage bottomBorderColour="border-b-error-90" icon={<div className="border-4 border-error-90 h-10 m-auto pt-0.5 rounded-full w-10">
              <WrongIcon className="m-auto fill-error-90 stroke-error-90" width={28} />
            </div>} image={DiseaseBlur} text={t('no_blur')} data-sentry-element="SampleImage" data-sentry-source-file="DiseaseUpload.tsx" />
        <SampleImage bottomBorderColour="border-b-success-80" icon={<div className="h-10 p-1 pt-2 m-auto border-4 rounded-full border-success-80 w-fit">
              <RightIcon className="fill-success-80 stroke-success-80" width={24} />
            </div>} image={DiseaseClear} text={t('take_clear')} data-sentry-element="SampleImage" data-sentry-source-file="DiseaseUpload.tsx" />
      </div>
      <input accept=".jpg, .jpeg, .png" className="hidden" data-testid="ask-question-disease-upload-input" onChange={onChange} ref={diseaseInputRef} type="file" />
      <div className="fixed bottom-0 w-full p-4 m-auto bg-white max-w-7xl">
        <button className="flex items-center justify-center w-full p-4 text-white rounded-lg bg-primary-100" data-testid="ask-question-disease-upload" onClick={() => {
        trackCustomEvent(CustomEventTypes.ASK_HELP_TAKE_PHOTO_CTA_CLICKED);
        diseaseInputRef.current?.click();
      }}>
          <CameraIcon className="mr-4 fill-white h-7 w-7 stroke-neutral-70" data-sentry-element="CameraIcon" data-sentry-source-file="DiseaseUpload.tsx" />
          {t('take_photo')}
        </button>
      </div>
    </>;
};
export default DiseaseUpload;